import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAxesStore } from '../../store/useAxesStore';
import { useAuth } from '../../context/AuthContext';
import { X, LayoutGrid, Clock, Check } from 'lucide-react';

interface SavedPlan {
    id: string;
    plan_name: string;
    updated_at: string;
    plan_data: any;
}

interface SavedPlansModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const SavedPlansModal: React.FC<SavedPlansModalProps> = ({ isOpen, onClose }) => {
    const { user } = useAuth();
    const { loadPlan } = useAxesStore();
    const [plans, setPlans] = useState<SavedPlan[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

    const fetchPlans = async () => {
        if (!user) return;
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('user_saved_resplans')
                .select('id, plan_name, updated_at, plan_data')
                .eq('user_id', user.id)
                .order('updated_at', { ascending: false });

            if (error) throw error;
            if (data) setPlans(data);
        } catch (err) {
            console.error('Error fetching plans:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchPlans();
        }
    }, [isOpen]);

    const handleLoadPlan = async (planId: string) => {
        setLoadingPlan(planId);
        try {
            await loadPlan(planId);
            onClose();
        } catch (err) {
            console.error('Error loading plan:', err);
            alert('Failed to load plan.');
        } finally {
            setLoadingPlan(null);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-zinc-800">
                    <div>
                        <h2 className="text-xl font-bold text-white">My Saved Plans</h2>
                        <p className="text-sm text-zinc-400 mt-1">Load a saved plan to continue editing</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-zinc-500 hover:text-white transition-colors p-2"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-20">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
                        </div>
                    ) : plans.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-zinc-500">
                            <LayoutGrid size={48} className="mb-4 opacity-50" />
                            <p className="text-lg font-medium mb-2">No saved plans yet</p>
                            <p className="text-sm">Save your work from the Axes Explorer to see it here</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {plans.map((plan) => {
                                const areaRounded = plan.plan_data?.total_area_m2
                                    ? Math.round(plan.plan_data.total_area_m2)
                                    : '?';
                                const roomsCount = plan.plan_data?.nodes?.length || 0;
                                const isLoading = loadingPlan === plan.id;

                                return (
                                    <div
                                        key={plan.id}
                                        onClick={() => !isLoading && handleLoadPlan(plan.id)}
                                        disabled={isLoading}
                                        className={`group relative bg-zinc-800 border border-zinc-700 rounded-xl overflow-hidden cursor-pointer hover:border-indigo-500/50 hover:shadow-xl hover:shadow-indigo-500/10 transition-all duration-200 flex flex-col ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {/* Loading Overlay */}
                                        {isLoading && (
                                            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-10">
                                                <div className="flex flex-col items-center gap-2">
                                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500"></div>
                                                    <span className="text-xs text-white font-medium">Loading...</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Thumbnail */}
                                        <div className="aspect-video bg-zinc-700 flex items-center justify-center relative border-b border-zinc-700/50">
                                            <LayoutGrid className="text-zinc-600" size={48} strokeWidth={1} />
                                            <div className="absolute inset-0 bg-gradient-to-t from-zinc-800 via-transparent to-transparent opacity-60"></div>
                                        </div>

                                        {/* Info */}
                                        <div className="p-4 flex-1 flex flex-col">
                                            <h3 className="font-semibold text-base text-white mb-3 truncate">
                                                {plan.plan_name}
                                            </h3>

                                            <div className="flex flex-col gap-2 mt-auto shrink-0 text-xs text-zinc-400">
                                                <div className="flex items-center gap-2">
                                                    <LayoutGrid size={14} />
                                                    <span>{roomsCount} Rooms • {areaRounded}m²</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Clock size={14} />
                                                    <span>{new Date(plan.updated_at).toLocaleDateString()}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Success indicator when loading */}
                                        {isLoading && (
                                            <div className="absolute top-3 right-3">
                                                <div className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center">
                                                    <Check size={14} className="text-white" />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
